import { Dispatch, SetStateAction, useCallback } from 'react'

import { logError } from '@sequencehq/utils'
import { useStytchB2BClient } from '@stytch/react/b2b'
import posthog from 'posthog-js'

import { redirectToLogin } from 'lib/auth/session'
import { NavigationV3ContextType } from 'components/NavigationV3/NavigationV3Provider.types'

type UseNavigationV3Functions = ({
  setIsSwitchingWorkspace,
  toggleCollapsed
}: {
  setIsSwitchingWorkspace: Dispatch<SetStateAction<boolean>>
  toggleCollapsed: () => void
}) => {
  // The mobile-drawer functions (toggleMobileNav/closeMobileNav) are owned by
  // the provider, so this hook supplies everything else.
  functions: Omit<
    NavigationV3ContextType['functions'],
    'toggleMobileNav' | 'closeMobileNav'
  >
}

export const useNavigationV3Functions: UseNavigationV3Functions = ({
  setIsSwitchingWorkspace,
  toggleCollapsed
}) => {
  const stytch = useStytchB2BClient()

  const switchWorkspace = useCallback(
    async (props: { workspaceId: string }) => {
      try {
        setIsSwitchingWorkspace(true)

        await stytch.session.exchange({
          organization_id: props.workspaceId,
          /**
           * `session_duration_minutes` is set to 45 days to match the session length set by
           * the backend when logging in.
           * Thread: https://sequenceslack.slack.com/archives/C0393LQDTJS/p1712223109829969
           */
          session_duration_minutes: 64800
        })

        // Redirect to home page which will trigger a full page reload
        window.location.href = '/'
      } catch (e) {
        logError(e)
        setIsSwitchingWorkspace(false)
      }
    },
    [setIsSwitchingWorkspace, stytch.session]
  )

  const handleLogOut = useCallback(async () => {
    try {
      await stytch.session.revoke()
      posthog.reset()
      redirectToLogin()
    } catch (e) {
      logError(e)
    }
  }, [stytch])

  const toggleNavigationCollapsed = useCallback(() => {
    toggleCollapsed()
  }, [toggleCollapsed])

  return {
    functions: {
      switchWorkspace,
      handleLogOut,
      toggleNavigationCollapsed
    }
  }
}
